import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { FormControl } from '@angular/forms';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { LoginFleetInfo } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { UpdateTags } from '@app-shared/actions/tags.action';
import { State } from '@app-shared/reducers';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, skip, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FlatTag {
  tagId: number;
  tagName: string;
  tagStatus: 'ACTIVE' | 'INACTIVE';
  parentTagId: number | null;
  rootTagId: number;
  entityName: string;
  extTagId: string | null;
  path: string | null;
  disabled: boolean;
  highlightedName?: string;
  // computed
  _hasChildren: boolean;
  _childCount: number;
}

export interface SearchTag extends FlatTag {
  breadcrumb: { tagName: string }[];
  breadcrumbSegments: { name: string; isMatch: boolean }[];
}

export interface TagGroup {
  label: string; // attribute name (UPPER) or '' for floating-selected group
  attrIndex: number; // index into attrStore; -1 for floating group
  tags: FlatTag[];
}

export interface DrillEntry {
  tagName: string;
  rootTagId: number;
  attrIndex: number;
}

interface AttrStore {
  attributeId: number;
  attributeName: string;
  attributeType: 'DATA' | 'ACCESS';
  optionGroupLabel?: string;
  allRawTags: any[]; // full flat API list — never mutated
  visibleTags: FlatTag[]; // currently shown at the active drill level
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-tagging-filter-v3',
  templateUrl: './tagging-filter-v3.component.html',
  styleUrls: ['./tagging-filter-v3.component.scss'],
})
export class TaggingFilterV3Component implements OnInit, OnChanges, OnDestroy {
  // ── Inputs ────────────────────────────────────────────────────────────────
  @Input() entityType = '';
  @Input() dropDownType: 'filter' | 'assign' = 'assign';
  @Input() public assignedTagList: any[] = [];
  @Input() public numOfTagsToDisplay = 1;
  @Input() attributeType = '';
  @Input() roleId = '';
  @Input() isClearDropDown = false;
  @Input() isUserPage = false;
  @Input() appearance = '';
  @Input() styles: any = {};
  @Input() label = '';
  @Input() isWideFeild = false;
  @Input() public filterType: 'flat' | 'box' = 'flat';
  @Input() public dropDirection: 'up' | 'down' = 'down';
  @Input() isReactMicrofrontend: boolean;

  // ── Outputs ───────────────────────────────────────────────────────────────
  @Output() public selectedTags = new EventEmitter<number[]>();
  @Output() public dropdownClosed = new EventEmitter<void>();

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('triggerRef') triggerRef!: ElementRef;

  // ── FormControl (validation only — not tied to mat-select) ───────────────
  public tags = new FormControl<number[]>([], this.maxTagsValidator());

  // ── State ─────────────────────────────────────────────────────────────────
  public panelOpen = false;
  public getAllTagsLoader = false;
  public totalTags = 0;
  public searchQuery = '';
  public currentLevel = 1;
  public drillStack: DrillEntry[] = [];
  public selectedIds: number[] = [];
  public tagList: string[] = [];
  public visibleGroups: TagGroup[] = [];
  public filteredAttrList: any[] = [];

  // ── Search state ──────────────────────────────────────────────────────────
  public isSearchMode = false;
  public isSearchLoading = false;
  public filteredSearchTags: SearchTag[] = [];
  public searchDisplayCount = 10;
  private readonly SEARCH_PAGE_SIZE = 10;
  private searchTerms = new Subject<string>();

  // ── Private ───────────────────────────────────────────────────────────────
  private attrStore: AttrStore[] = [];
  private allowedUserTags: any[] = [];
  private currentFleetId = '';
  private currentFleetInfo!: LoginFleetInfo;
  private ngUnsubscribe = new Subject<void>();
  private isApplyingAssignedList = false;

  constructor(
    private taggingService: TaggingService,
    private accessService: AccessService,
    public dataService: DataService,
    private translate: TranslateService,
    private store: Store<State>,
    private cdRef: ChangeDetectorRef,
    private durationRangeService: DurationRangeService // private elRef: ElementRef
  ) {}

  // ══════════════════════════════════════════════════════════════════════════
  //  Lifecycle
  // ══════════════════════════════════════════════════════════════════════════

  async ngOnInit() {
    this.currentFleetId = this.dataService._currentFleet.getValue();
    await this.loadTags();
    this.initSearch();

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(async (id: string) => {
      if (id) {
        this.currentFleetId = id;
        await this.loadTags();
      }
    });
  }

  public async ngOnChanges(changes: SimpleChanges) {
    if (changes['assignedTagList']?.currentValue) {
      this.applyAssignedList();
    }
    if (changes['isClearDropDown']?.currentValue && this.isClearDropDown) {
      this.clearSelection();
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Panel open / close
  // ══════════════════════════════════════════════════════════════════════════

  public togglePanel(): void {
    if (this.getAllTagsLoader || this.totalTags === 0) return;
    this.panelOpen ? this.closePanel() : this.openPanel();
  }

  public openPanel(): void {
    this.panelOpen = true;
    this.searchQuery = '';
    this.isSearchMode = false;
    this.filteredSearchTags = [];
    this.drillStack = [];
    this.currentLevel = 1;
    this.resetVisibleToRoot();

    setTimeout(() => {
      // Focus search
      this.searchInputRef?.nativeElement?.focus();
    }, 0);
  }

  public closePanel(): void {
    if (!this.panelOpen) return;
    this.panelOpen = false;
    this.searchQuery = '';
    this.isSearchMode = false;
    this.filteredSearchTags = [];
    this.drillStack = [];
    this.currentLevel = 1;
    this.resetVisibleToRoot();
    this.dropdownClosed.emit();
  }

  get overlayWidth(): number {
    const viewportWidth = window.innerWidth;
    if (viewportWidth <= 767) {
      return viewportWidth - 16;
    }
    if (this.isWideFeild && this.triggerRef) {
      return Math.max(this.triggerRef.nativeElement.offsetWidth, 300);
    }
    return 300;
  }

  get overlayPositions(): ConnectionPositionPair[] {
    const alignRight = this.dropDownType === 'filter';

    const startX = alignRight ? 'end' : 'start';
    const fallbackX = alignRight ? 'start' : 'end';

    if (this.dropDirection === 'up') {
      return [
        new ConnectionPositionPair({ originX: startX, originY: 'top' }, { overlayX: startX, overlayY: 'bottom' }, 0, -4),
        new ConnectionPositionPair({ originX: fallbackX, originY: 'top' }, { overlayX: fallbackX, overlayY: 'bottom' }, 0, -4),
        new ConnectionPositionPair({ originX: startX, originY: 'bottom' }, { overlayX: startX, overlayY: 'top' }, 0, 4),
        new ConnectionPositionPair({ originX: fallbackX, originY: 'bottom' }, { overlayX: fallbackX, overlayY: 'top' }, 0, 4),
      ];
    }

    return [
      new ConnectionPositionPair({ originX: startX, originY: 'bottom' }, { overlayX: startX, overlayY: 'top' }, 0, 4),
      new ConnectionPositionPair({ originX: fallbackX, originY: 'bottom' }, { overlayX: fallbackX, overlayY: 'top' }, 0, 4),
      new ConnectionPositionPair({ originX: startX, originY: 'top' }, { overlayX: startX, overlayY: 'bottom' }, 0, -4),
      new ConnectionPositionPair({ originX: fallbackX, originY: 'top' }, { overlayX: fallbackX, overlayY: 'bottom' }, 0, -4),
    ];
  }

  /** Triggered by CDK overlay backdrop */
  public onOutsideClick(): void {
    if (this.panelOpen) {
      this.closePanel();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Search (client-side)

  private initSearch(): void {
    this.searchTerms.pipe(debounceTime(200), takeUntil(this.ngUnsubscribe)).subscribe((term: string) => {
      const q = term.trim().toLowerCase();

      if (!q) {
        this.filteredSearchTags = [];
        this.isSearchLoading = false;
        this.cdRef.detectChanges();
        return;
      }

      this.isSearchLoading = true;

      const allTags = this.attrStore.flatMap((attr) => attr.allRawTags || []);

      const matchedTags = allTags.filter((tag: any) => {
        const tagNameMatch = tag.tagName?.toLowerCase().includes(q);
        const breadcrumbPath = this.buildBreadcrumbFromPath(tag);
        const breadcrumbMatch = breadcrumbPath.some((b) => b.tagName.toLowerCase().includes(q));
        return tagNameMatch || breadcrumbMatch;
      });

      this.filteredSearchTags = matchedTags.map((tag: any) => {
        const breadcrumb = this.buildBreadcrumbFromPath(tag);
        const segments = this.buildBreadcrumbSegments(breadcrumb, q);
        const highlightedName = tag.tagName?.replace(new RegExp(`(${q})`, 'gi'), '<b>$1</b>');
        const annotated = this.annotate([tag])[0];

        return {
          ...annotated,
          breadcrumb,
          breadcrumbSegments: segments,
          highlightedName,
        } as SearchTag;
      });

      this.isSearchLoading = false;
      this.cdRef.detectChanges();
    });
  }

  private buildBreadcrumbFromPath(tag: any): { tagName: string }[] {
    if (!tag.path) return this.buildBreadcrumbFromParent(tag);

    const pathIds = tag.path.split('.').map(Number);

    if (pathIds.some(isNaN)) {
      return this.buildBreadcrumbFromParent(tag);
    }

    const result = pathIds
      .map((id: number) => this.allAttrTagList.find((t: any) => t.rootTagId === id))
      .filter(Boolean)
      .map((t: any) => ({ tagName: t.tagName }));

    return result.length > 0 ? result : this.buildBreadcrumbFromParent(tag);
  }

  private buildBreadcrumbFromParent(tag: any): { tagName: string }[] {
    const chain: { tagName: string }[] = [];
    const visited = new Set<number>();
    let current: any = tag;

    while (current) {
      if (visited.has(current.rootTagId)) break;
      visited.add(current.rootTagId);
      chain.unshift({ tagName: current.tagName });
      if (current.parentTagId == null) break;
      current = this.allAttrTagList.find((t: any) => t.rootTagId === current.parentTagId);
    }

    return chain;
  }

  public get visibleSearchTags(): SearchTag[] {
    return this.filteredSearchTags.slice(0, this.searchDisplayCount);
  }

  public onSearchListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && this.searchDisplayCount < this.filteredSearchTags.length) {
      this.searchDisplayCount += this.SEARCH_PAGE_SIZE;
      this.cdRef.detectChanges();
    }
  }

  public onSearch(): void {
    const q = this.searchQuery.trim();

    if (!q) {
      this.isSearchMode = false;
      this.filteredSearchTags = [];
      this.searchDisplayCount = this.SEARCH_PAGE_SIZE;
      this.resetVisibleToRoot();
      return;
    }

    this.isSearchMode = true;
    this.isSearchLoading = true;
    this.searchDisplayCount = this.SEARCH_PAGE_SIZE;
    this.searchTerms.next(q);
  }

  /** Clear the search input and return to normal browse mode */
  public clearSearch(): void {
    this.searchQuery = '';
    this.isSearchMode = false;
    this.isSearchLoading = false;
    this.filteredSearchTags = [];
    this.searchDisplayCount = this.SEARCH_PAGE_SIZE;
    this.drillStack = [];
    this.currentLevel = 1;
    this.resetVisibleToRoot();
    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 50);
  }

  /**
   * Build breadcrumb segments, marking which segments contain the search term.
   * For very long breadcrumbs (> 5 items), only the last 5 are shown
   * (the template prepends "..." automatically).
   */
  private buildBreadcrumbSegments(breadcrumb: { tagName: string }[], query: string): { name: string; isMatch: boolean }[] {
    if (!breadcrumb || breadcrumb.length === 0) return [];

    const MAX_VISIBLE = 5;
    const visible = breadcrumb.length > MAX_VISIBLE ? breadcrumb.slice(-MAX_VISIBLE) : breadcrumb;

    return visible.map((b) => ({
      name: b.tagName,
      isMatch: query ? b.tagName.toLowerCase().includes(query) : false,
    }));
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Row click — cascade: selects tag + ALL descendants
  // ══════════════════════════════════════════════════════════════════════════

  public onRowClick(tag: FlatTag): void {
    if (tag.disabled || this.isTagDisabled(tag)) return;

    const alreadySelected = this.selectedIds.includes(tag.tagId);
    const descendantIds = this.getAllDescendantTagIds(tag);
    const idsToToggle = [tag.tagId, ...descendantIds];

    if (alreadySelected) {
      this.selectedIds = this.selectedIds.filter((id) => !idsToToggle.includes(id));
    } else {
      const toAdd = idsToToggle.filter((id) => !this.selectedIds.includes(id));
      this.selectedIds = [...this.selectedIds, ...toAdd];
    }

    this.syncTagListNames();
    this.tags.setValue(this.selectedIds, { emitEvent: false });
    this.selectedTags.emit(this.getEmitIds());
    this.applyAccessTagDisable();
    this.buildVisibleGroups();
    this.cdRef.detectChanges();
  }

  public isSelected(tag: FlatTag): boolean {
    return this.selectedIds.includes(tag.tagId);
  }

  /** Tag not selected but ≥1 descendant IS selected */
  public isIndeterminate(tag: FlatTag): boolean {
    if (this.isSelected(tag)) return false;
    return this.getAllDescendantTagIds(tag).some((id) => this.selectedIds.includes(id));
  }

  public unselectAll(): void {
    this.selectedIds = [];
    this.syncTagListNames();
    this.tags.setValue(this.selectedIds, { emitEvent: false });
    this.selectedTags.emit(this.getEmitIds());
    this.applyAccessTagDisable();
    this.buildVisibleGroups();
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Drill-down navigation
  // ══════════════════════════════════════════════════════════════════════════

  public drillDown(attrIndex: number, tag: FlatTag): void {
    if (!tag._hasChildren) return;

    if (attrIndex === -1) {
      attrIndex = this.attrStore.findIndex((a) => a.allRawTags.some((r: any) => r.tagId === tag.tagId));
      if (attrIndex === -1) return;
    }

    const attr = this.attrStore[attrIndex];
    attr.visibleTags = this.getChildren(this.annotate(attr.allRawTags), tag.rootTagId);

    this.drillStack = [...this.drillStack, { tagName: tag.tagName, rootTagId: tag.rootTagId, attrIndex }];
    this.currentLevel = (tag.path ? tag.path.split('.').length : 1) + 1;
    this.buildVisibleGroups();
  }

  public goBack(): void {
    if (!this.drillStack.length) return;
    this.applyStack(this.drillStack.slice(0, -1));
  }

  public goToRoot(): void {
    this.resetVisibleToRoot();
  }

  public goToCrumb(index: number): void {
    if (index === this.drillStack.length - 1) return;
    this.applyStack(this.drillStack.slice(0, index + 1));
  }

  private applyStack(stack: DrillEntry[]): void {
    if (!stack.length) {
      this.resetVisibleToRoot();
      return;
    }

    const target = stack[stack.length - 1];
    const attr = this.attrStore[target.attrIndex];
    attr.visibleTags = this.getChildren(this.annotate(attr.allRawTags), target.rootTagId);

    this.attrStore.forEach((a, idx) => {
      if (idx !== target.attrIndex) {
        a.visibleTags = this.getRootTags(this.annotate(a.allRawTags));
      }
    });

    this.drillStack = stack;
    const rawTag = attr.allRawTags.find((r: any) => r.rootTagId === target.rootTagId);
    this.currentLevel = (rawTag?.path ? rawTag.path.split('.').length : stack.length) + 1;
    this.buildVisibleGroups();
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  visibleGroups
  // ══════════════════════════════════════════════════════════════════════════

  private buildVisibleGroups(): void {
    const groups: TagGroup[] = [];

    // Pre-build a single descendant map for all attrs to avoid re-annotating per tag
    const descMap = new Map<number, number[]>();
    for (const attr of this.attrStore) {
      const annotated = this.annotate(attr.allRawTags);
      const visited = new Set<number>();
      const collect = (rootTagId: number): number[] => {
        if (visited.has(rootTagId)) return [];
        visited.add(rootTagId);
        const kids = annotated.filter((t) => t.parentTagId === rootTagId);
        return kids.flatMap((c) => (c.tagStatus === 'INACTIVE' ? [] : [c.tagId, ...collect(c.rootTagId)]));
      };
      annotated.forEach((t) => {
        if (!descMap.has(t.tagId)) {
          descMap.set(t.tagId, collect(t.rootTagId));
        }
      });
    }

    const hasSelectedDescendant = (tag: FlatTag) => descMap.get(tag.tagId)?.some((id) => this.selectedIds.includes(id)) ?? false;

    if (this.drillStack.length === 0) {
      const floating: FlatTag[] = [];
      const added = new Set<number>();

      this.attrStore.forEach((attr) => {
        attr.visibleTags.forEach((tag) => {
          const isSelected = this.selectedIds.includes(tag.tagId) || hasSelectedDescendant(tag);
          if (isSelected && !added.has(tag.tagId)) {
            floating.push(tag);
            added.add(tag.tagId);
          }
        });
      });

      if (floating.length) {
        groups.push({ label: '', attrIndex: -1, tags: floating.sort((a, b) => a.tagName.localeCompare(b.tagName)) });
      }

      this.attrStore.forEach((attr, idx) => {
        const unsel = attr.visibleTags.filter((tag) => {
          return !this.selectedIds.includes(tag.tagId) && !hasSelectedDescendant(tag);
        });
        if (unsel.length) {
          groups.push({ label: (attr.optionGroupLabel || attr.attributeName).toUpperCase(), attrIndex: idx, tags: unsel });
        }
      });
    } else {
      const entry = this.drillStack[this.drillStack.length - 1];
      const attr = this.attrStore[entry.attrIndex];
      if (attr?.visibleTags?.length) {
        groups.push({ label: '', attrIndex: entry.attrIndex, tags: attr.visibleTags });
      }
    }

    this.visibleGroups = groups;
  }

  private resetVisibleToRoot(): void {
    this.attrStore.forEach((attr) => {
      attr.visibleTags = this.getRootTags(this.annotate(attr.allRawTags));
    });
    this.drillStack = [];
    this.currentLevel = 1;
    this.buildVisibleGroups();
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Tree helpers
  // ══════════════════════════════════════════════════════════════════════════

  private annotate(rawTags: any[]): FlatTag[] {
    const rootIdSet = new Set(rawTags.map((t: any) => t.rootTagId));
    const childMap = new Map<number, number[]>();

    rawTags.forEach((t: any) => {
      if (t.parentTagId != null && rootIdSet.has(t.parentTagId)) {
        if (!childMap.has(t.parentTagId)) childMap.set(t.parentTagId, []);
        childMap.get(t.parentTagId)!.push(t.rootTagId);
      }
    });

    const countDesc = (id: number): number => {
      const kids = childMap.get(id) ?? [];
      return kids.reduce((s: number, k: number) => s + 1 + countDesc(k), 0);
    };

    return rawTags.map((t: any) => ({
      ...t,
      disabled: t.disabled ?? false,
      _hasChildren: childMap.has(t.rootTagId),
      _childCount: countDesc(t.rootTagId),
    })) as FlatTag[];
  }

  private getRootTags(annotated: FlatTag[]): FlatTag[] {
    const presentRootIds = new Set(annotated.map((t) => t.rootTagId));
    // A tag is a visible root if it has no parent OR its parent was filtered out of the list
    return annotated.filter((t) => t.parentTagId === null || !presentRootIds.has(t.parentTagId));
  }

  private getChildren(annotated: FlatTag[], parentRootTagId: number): FlatTag[] {
    return annotated.filter((t) => t.parentTagId === parentRootTagId);
  }

  // private getAllDescendantTagIds(tag: FlatTag): number[] {
  //   for (const attr of this.attrStore) {
  //     const annotated = this.annotate(attr.allRawTags);
  //     const node = annotated.find((t) => t.tagId === tag.tagId);
  //     if (!node) continue;

  //     const collect = (rootTagId: number): number[] => {
  //       const kids = annotated.filter((t) => t.parentTagId === rootTagId);
  //       return kids.flatMap((c) => {
  //         // If child is INACTIVE, skip it and all its descendants
  //         if (c.tagStatus === 'INACTIVE') return [];
  //         return [c.tagId, ...collect(c.rootTagId)];
  //       });
  //     };
  //     return collect(node.rootTagId);
  //   }
  //   return [];
  // }

  private getAllDescendantTagIds(tag: FlatTag): number[] {
    for (const attr of this.attrStore) {
      // Use already-annotated visibleTags + allRawTags instead of re-annotating
      const annotated = this.annotate(attr.allRawTags);
      const node = annotated.find((t) => t.tagId === tag.tagId);
      if (!node) continue;

      const visited = new Set<number>(); // ← prevent infinite loops from data cycles
      const collect = (rootTagId: number): number[] => {
        if (visited.has(rootTagId)) return []; // ← break cycle
        visited.add(rootTagId);
        const kids = annotated.filter((t) => t.parentTagId === rootTagId);
        return kids.flatMap((c) => {
          if (c.tagStatus === 'INACTIVE') return [];
          return [c.tagId, ...collect(c.rootTagId)];
        });
      };
      return collect(node.rootTagId);
    }
    return [];
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Selection helpers
  // ══════════════════════════════════════════════════════════════════════════

  private syncTagListNames(): void {
    const topLevelIds = this.getTopLevelSelectedIds();
    this.tagList = topLevelIds.reduce((names: string[], id) => {
      for (const attr of this.attrStore) {
        const match = attr.allRawTags.find((t: any) => t.tagId === id);
        if (match) {
          names.push(match.tagName);
          break;
        }
      }
      return names;
    }, []);
  }

  private getEmitIds(): number[] {
    return this.durationRangeService.oldedVersionTagging ? this.selectedIds : this.getTopLevelSelectedIds();
  }

  private getTopLevelSelectedIds(): number[] {
    const selectedSet = new Set(this.selectedIds);
    return this.selectedIds.filter((id) => {
      const tag = this.allAttrTagList.find((t: any) => t.tagId === id);
      if (!tag) return true;
      return !this.hasSelectedAncestor(tag, selectedSet);
    });
  }

  private hasSelectedAncestor(tag: any, selectedSet: Set<number>): boolean {
    const visited = new Set<number>();
    let parentRootTagId = tag.parentTagId;
    while (parentRootTagId != null) {
      if (visited.has(parentRootTagId)) break;
      visited.add(parentRootTagId);
      const parent = this.allAttrTagList.find((t: any) => t.rootTagId === parentRootTagId);
      if (!parent) break;
      if (selectedSet.has(parent.tagId)) return true;
      parentRootTagId = parent.parentTagId;
    }
    return false;
  }

  private applyAccessTagDisable(): void {
    if (this.dropDownType === 'filter') return;
    const selAccess = this.allowedUserTags.filter((t) => this.selectedIds.includes(t.tagId));
    const disableLast = this.allowedUserTags.length > 0 && selAccess.length === 1;
    this.attrStore.forEach((attr) => {
      attr.visibleTags.forEach((t: any) => {
        t.disabled = disableLast && t.tagId === selAccess[0]?.tagId;
      });
    });
  }

  // private applyAssignedList(): void {
  //   this.selectedIds = (this.assignedTagList || []).map((t: any) => t.tagId);
  //   this.syncTagListNames();
  //   this.tags.setValue(this.selectedIds, { emitEvent: false });
  //   this.buildVisibleGroups();

  //   this.selectedTags.emit(this.selectedIds);
  //   if (!this.isUserPage) this.applyAssignedList();
  // }

  private applyAssignedList(): void {
    if (this.isApplyingAssignedList) return; // ← guard
    this.isApplyingAssignedList = true;
    try {
      const baseIds: number[] = (this.assignedTagList || []).map((t: any) => t.tagId);
      const expandedIds = [...baseIds];
      for (const id of baseIds) {
        const tag = this.allAttrTagList.find((t: any) => t.tagId === id);
        if (tag) {
          const descIds = this.getAllDescendantTagIds(tag as FlatTag);
          descIds.forEach((dId) => {
            if (!expandedIds.includes(dId)) expandedIds.push(dId);
          });
        }
      }
      this.selectedIds = expandedIds;
      this.syncTagListNames();
      this.tags.setValue(this.selectedIds, { emitEvent: false });
      this.buildVisibleGroups();
      this.selectedTags.emit(this.getEmitIds());
    } finally {
      this.isApplyingAssignedList = false;
    }
  }

  private clearSelection(): void {
    this.selectedIds = [];
    this.tagList = [];
    this.searchQuery = '';
    this.isSearchMode = false;
    this.filteredSearchTags = [];
    this.tags.setValue([], { emitEvent: false });
    this.attrStore.forEach((attr) => {
      attr.visibleTags.forEach((t: any) => (t.disabled = false));
      attr.allRawTags.forEach((t: any) => (t.disabled = false));
    });
    this.resetVisibleToRoot();
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Public helpers / API compat
  // ══════════════════════════════════════════════════════════════════════════

  public getTooltip(tag: FlatTag): string | null {
    return this.isTagDisabled(tag) ? this.translate.instant('DisabledTagToolTip') : null;
  }

  public editMode() {}
  public doneMode() {}
  public assignSelectedDropdownValue() {
    this.applyAssignedList();
  }
  public unselectTags() {
    this.unselectAll();
  }
  public get allAttrTagList() {
    return this.attrStore.flatMap((a) => a.allRawTags);
  }
  public get filteredAttribute() {
    return this.visibleGroups;
  }

  private maxTagsValidator() {
    const limit: number = this.durationRangeService.maxAllowedTags;
    return () => {
      if (!this.selectedIds) return null;
      const topLevelCount = this.getTopLevelSelectedIds().length;
      return topLevelCount > limit ? { maxTagsLimit: true, maxAllowedTags: limit } : null;
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  API — load tags
  // ══════════════════════════════════════════════════════════════════════════

  private async loadTags(): Promise<void> {
    try {
      this.getAllTagsLoader = true;
      this.totalTags = 0;
      this.drillStack = [];
      this.currentLevel = 1;
      this.allowedUserTags = [];

      // Re-read login info each load — startup service rewrites userTags
      // (with API-sourced ids) shortly after SSO login
      const { fleets = [] } = this.accessService.getLoginInfo();
      this.currentFleetInfo = fleets.find((x: any) => x.fleetId === this.currentFleetId);

      const params: any = { limit: 500, offset: 0 };
      if (this.entityType) params['entityNames[]'] = this.entityType;
      if (this.roleId) params['roleIds[]'] = this.roleId;
      if (this.attributeType) params['attributeType'] = this.attributeType;

      const res = await this.taggingService.getAttributes(params).toPromise();

      const expanded: any[] = (res?.data || []).flatMap((attr: any) => {
        const { entities = [], tags: rawTags } = attr;
        const tags = rawTags ?? [];
        this.totalTags += tags.length;
        if (entities.length < 2) return [{ ...attr, tags }];
        return entities.map((e: string) => ({
          ...attr,
          optionGroupLabel: `${attr.attributeName} (${e})`,
          tags: tags.filter((t: any) => t.entityName === e),
        }));
      });

      expanded.forEach((a) => a.tags?.sort((x: any, y: any) => x.tagName.localeCompare(y.tagName)));

      const [dataAttrs, accessAttrs] = expanded.reduce(
        (acc: [any[], any[]], a: any) => {
          acc[a.attributeType === 'DATA' ? 0 : 1].push(a);
          return acc;
        },
        [[], []]
      );

      // SSO login info carries userTags ids as strings while the attributes API
      // returns numbers — compare as numbers to avoid dropping all ACCESS tags
      const userAttrIds = [...new Set((this.currentFleetInfo?.userTags || []).map((t: any) => Number(t.attributeId)))];
      const userTagIds = (this.currentFleetInfo?.userTags || []).map((t: any) => Number(t.tagId));
      let allowedAccess: any[];

      if (userAttrIds.length) {
        allowedAccess = accessAttrs
          .filter((a: any) => userAttrIds.includes(Number(a.attributeId)))
          .map((a: any) => {
            const allowed = (a.tags || []).filter((t: any) => userTagIds.includes(Number(t.tagId)));
            this.allowedUserTags.push(...allowed);
            return { ...a, tags: allowed };
          });
        // if (!this.assignedTagList.length) this.assignedTagList = [...this.allowedUserTags];
      } else {
        allowedAccess = accessAttrs;
      }

      const combined = [...dataAttrs, ...allowedAccess];
      this.filteredAttrList = combined;

      this.attrStore = combined.map((attr) => {
        const annotated = this.annotate(attr.tags || []);
        return {
          attributeId: attr.attributeId,
          attributeName: attr.attributeName,
          attributeType: attr.attributeType,
          optionGroupLabel: attr.optionGroupLabel,
          allRawTags: attr.tags || [],
          visibleTags: this.getRootTags(annotated),
        } as AttrStore;
      });

      this.getAllTagsLoader = false;
      if (!this.isUserPage) this.applyAssignedList();

      this.store.dispatch(new UpdateTags({ allTags: this.attrStore.flatMap((a) => a.allRawTags) }));
      this.buildVisibleGroups();
      this.cdRef.detectChanges();
    } catch (err) {
      this.getAllTagsLoader = false;
      throw err;
    }
  }
  removeTag(tagName: string): void {
    // Find a tag that is BOTH named `tagName` AND currently selected
    const match = this.allAttrTagList.find((t) => t.tagName === tagName && this.selectedIds.includes(t.tagId));
    if (match) {
      this.onRowClick(match);
    }
  }
  public isTagDisabled(tag: FlatTag): boolean {
    return this.dropDownType === 'assign' && tag.tagStatus === 'INACTIVE';
  }
}
